/* eslint-disable react/no-unescaped-entities */
import { HOME } from '@/constants/testIds';

const STORY_TAGS = [
  'a memory',
  'a business',
  'a movement',
  'a joke',
  'a belief',
  'a passion',
  'a moment that matters',
];

const AboutSection = () => (
  <section
    id="about"
    data-testid={HOME.aboutSection}
    className="relative bg-[#FFF7EE] text-black py-28 px-6 sm:px-12 overflow-hidden border-t-2 border-black/5"
  >
    {/* Soft orange flourish */}
    <div className="pointer-events-none absolute -top-20 -right-20 w-[28rem] h-[28rem] rounded-full bg-brand-orange/10 blur-3xl" />

    <div className="max-w-6xl mx-auto relative">
      {/* Eyebrow + headline */}
      <div className="text-center mb-14">
        <p className="font-body text-xs sm:text-sm tracking-[0.4em] uppercase text-brand-orange mb-4">
          About me
        </p>
        <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-[-0.02em] max-w-4xl mx-auto">
          More Than Shirts.<br />
          I Build <span className="text-brand-orange">Ideas You Can Wear.</span>
        </h2>
      </div>

      {/* Founder card + intro side-by-side */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14 items-start mb-16">
        <div className="md:col-span-4 order-2 md:order-1">
          <div className="relative">
            <div className="relative aspect-[4/5] rounded-[2rem] bg-black border-4 border-black shadow-[12px_12px_0_#FF6600] overflow-hidden">
              <img
                src="/brand/mascot-full.png"
                alt="Roger — founder of Shirt Happens Studio"
                className="absolute inset-0 w-full h-full object-contain p-8"
                draggable="false"
              />
            </div>
            <div className="absolute -top-5 -right-5 bg-brand-orange text-black font-display font-black text-xs uppercase tracking-[0.2em] px-4 py-2 rounded-full rotate-[8deg] shadow-[4px_4px_0_#000]">
              Founder · Roger
            </div>
          </div>
        </div>

        <div className="md:col-span-8 order-1 md:order-2 font-body text-lg leading-[1.7] text-black/80 space-y-5">
          <p className="font-display font-black text-2xl sm:text-3xl text-black leading-tight">
            Hi, I'm Roger, founder of Shirt Happens Studio.
          </p>
          <p>
            If there's one thing people have always known about me, it's this:
            <br />
            <strong className="text-black">I've always been creative.</strong>
          </p>
          <p>
            I've spent years creating, building, designing, learning, and dreaming.
            From graphic design and branding to websites, content creation, animation,
            apparel, and countless creative projects — I've always had ideas flowing
            through my mind.
          </p>
          <p className="text-black/60 italic">Sometimes… too many ideas.</p>
        </div>
      </div>

      {/* Pull quote from Tarvis */}
      <figure className="relative max-w-3xl mx-auto my-16 sm:my-20 px-6 sm:px-10 py-10 rounded-[2rem] bg-black text-white shadow-[12px_12px_0_#FF6600]">
        <span aria-hidden="true" className="absolute -top-6 -left-3 sm:-left-6 font-display font-black text-7xl sm:text-8xl text-brand-orange leading-none">
          &ldquo;
        </span>
        <blockquote className="font-display font-bold text-2xl sm:text-3xl leading-tight">
          You are a jack of all trades, but a master of none.
        </blockquote>
        <figcaption className="mt-5 font-body text-xs sm:text-sm uppercase tracking-[0.3em] text-white/50">
          — Tarvis, a good friend
        </figcaption>
      </figure>

      {/* Story continues */}
      <div className="max-w-3xl mx-auto font-body text-lg leading-[1.7] text-black/80 space-y-5">
        <p>
          A good friend of mine, Tarvis, once told me that.
          At first, those words hit hard. Not because he meant to tear me down — he didn't.
          He was telling me something I needed to hear. He saw something in me that I was
          struggling to admit to myself.
        </p>
        <div className="font-display font-bold text-xl sm:text-2xl text-black leading-snug py-4">
          I was constantly moving.<br />
          One idea to the next.<br />
          One project to the next.<br />
          One passion to the next.<br />
          <span className="text-brand-orange">Always creating…</span><br />
          But rarely slowing down long enough to master one thing.
        </div>
        <p>
          That conversation changed something in me.
        </p>
        <p>
          I realized creativity without focus can become chaos. And while I loved creating
          many things, there was one thing that kept calling me back:
          <strong className="text-black"> bringing people's ideas to life through design.</strong>
        </p>
        <p>
          That's where <strong className="text-black">Shirt Happens Studio</strong> was born.
        </p>
        <p>
          Not just as a t-shirt business.
          <br />
          Not as a side hustle.
          <br />
          But as a creative studio built around one mission:
        </p>
        <p className="font-display font-black text-2xl sm:text-3xl text-brand-orange leading-tight">
          Turning ideas into something real.
        </p>

        <p>I believe every design tells a story. A shirt can represent:</p>
      </div>

      {/* Tag chips */}
      <div className="max-w-3xl mx-auto flex flex-wrap gap-2 sm:gap-3 my-8">
        {STORY_TAGS.map((tag) => (
          <span
            key={tag}
            className="inline-flex items-center font-display font-bold text-xs sm:text-sm uppercase tracking-wide bg-white border-2 border-black rounded-full px-4 py-2 shadow-[3px_3px_0_#FF6600]"
          >
            {tag}
          </span>
        ))}
      </div>

      <div className="max-w-3xl mx-auto font-body text-lg leading-[1.7] text-black/80 space-y-5">
        <p>
          Sometimes people come to me with a complete vision. Sometimes they come with
          nothing more than a rough idea in their head. That's where I come in.
        </p>
        <p>
          I love taking raw ideas — the kind that exist only in someone's imagination —
          and transforming them into something they can see, hold, wear, and be proud of.
        </p>
        <p>That process never gets old to me.</p>
        <p className="font-display font-bold text-xl text-black leading-snug">
          Every logo.<br />
          Every shirt.<br />
          Every design.<br />
          <span className="text-brand-orange">It starts with trust.</span>
        </p>
        <p>
          When someone shares an idea with me, they're sharing something personal.
          I don't take that lightly.
        </p>
        <p>
          At Shirt Happens Studio, I combine creativity, craftsmanship, and attention to
          detail to create apparel and designs that feel meaningful — not generic.
        </p>
        <p>
          Because anyone can print a shirt. I want to build something better than that.
          I want to build designs that make people say:
        </p>
        <p className="font-display font-black text-xl sm:text-2xl text-black leading-tight border-l-4 border-brand-orange pl-5">
          "That's exactly what I imagined… or better."
        </p>
        <p>
          Today, I'm still creative. I still have big ideas. I still dream big.
        </p>
        <p>
          The difference now? <strong className="text-black">I've learned to focus.</strong>
          {' '}I've chosen to master the craft that allows me to serve others while doing
          what I love most.
        </p>
        <p>
          So whether you're a business owner building your brand, a family creating
          something memorable, or someone with a crazy idea you want brought to life…
        </p>
        <p className="font-display font-black text-xl text-black">
          You're in the right place.
        </p>
      </div>

      {/* Big closing tagline */}
      <div className="max-w-4xl mx-auto text-center mt-20 mb-10">
        <p className="font-display font-black text-3xl sm:text-5xl md:text-6xl leading-[1.05] tracking-[-0.02em] text-black">
          Because life happens.<br />
          Ideas happen.<br />
          And when they do —<br />
          <span className="inline-block bg-brand-orange text-black px-5 py-1 mt-3 -skew-x-3">
            Shirt Happens.
          </span>
        </p>
      </div>

      {/* Signature */}
      <div className="max-w-3xl mx-auto text-center font-body text-base text-black/70">
        <p className="font-display font-bold text-2xl text-black">— Roger</p>
        <p className="text-xs uppercase tracking-[0.3em] text-black/50 mt-2">
          Founder, Shirt Happens Studio
        </p>
      </div>

      {/* CTA row */}
      <div className="mt-14 flex flex-wrap gap-3 justify-center">
        <a
          href="#shop"
          className="inline-flex items-center gap-2 font-display font-bold text-xs sm:text-sm uppercase tracking-wide bg-black text-white rounded-full px-6 py-3 hover:bg-brand-orange transition-colors"
        >
          See the drops →
        </a>
        <a
          href="#custom"
          className="inline-flex items-center gap-2 font-display font-bold text-xs sm:text-sm uppercase tracking-wide border-2 border-black text-black rounded-full px-6 py-3 hover:bg-black hover:text-white transition-colors"
        >
          Send me an idea →
        </a>
      </div>
    </div>
  </section>
);

export default AboutSection;
